import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/**
 * Invoices that a bundle payment (cart) is in the middle of paying by ACH.
 *
 * A cart writes its payments rows only when the money arrives, so for the 3-5 days
 * an ACH debit takes there is no 'processing' payment on the invoice to show. This
 * is what shows it instead, so the invoice reads "processing" and is not offered for
 * payment again -- including to a co-parent who did not make the payment.
 *
 * It covers an invoice the other parent's cart is paying, too. With no carts it is an
 * empty set.
 * A failed read is an empty set too; this only decides what the page offers, and
 * an invoice paid twice still lands the extra in BHCS Cash when the cart settles.
 */
export async function fetchCartHeldInvoiceIds(invoiceId: string | null = null): Promise<string[]> {
  try {
    let query = supabase
      .from('payment_cart_items')
      .select('invoice_id, payment_carts!inner(status, charge_status)')
      .in('payment_carts.status', ['open', 'partially_funded'])
      .eq('payment_carts.charge_status', 'processing');
    if (invoiceId) query = query.eq('invoice_id', invoiceId);
    const { data: rows, error } = await query;
    if (error) return [];
    return ((rows ?? []) as Array<{ invoice_id: string }>).map((r) => r.invoice_id);
  } catch {
    return [];
  }
}

export function useCartHeldInvoiceIds(options: { enabled?: boolean; invoiceId?: string | null } = {}): Set<string> {
  const { enabled = true, invoiceId = null } = options;
  const { data } = useQuery<string[]>({
    queryKey: ['cart-held-invoices', invoiceId ?? 'all'],
    queryFn: () => fetchCartHeldInvoiceIds(invoiceId),
    enabled,
    staleTime: 30 * 1000,
  });
  return useMemo(() => new Set(Array.isArray(data) ? data : []), [data]);
}
